import { cx } from '@linaria/core';
import { useRef, useState, useEffect } from 'react';
import { TransitionGroup } from 'react-transition-group';
import { Drawer } from '@ui/kit';
import { useToggleState, SizeTransition } from '@utils/react';
import { courseBuilderStore } from '../../../../state/store.js';
import { createStore } from './_create-drawer.state.js';
import { transitionWrapperCls, panelTransitionCls, panelTransitionDurationMs, wrapperCls } from './_create-drawer.style.js';
import { BasePanel } from './base-panel.js';
import { CreateCourseSelectPanel } from './select-panel.js';
import { jsx, jsxs } from 'react/jsx-runtime';
import { useCreateCourseMutation } from '../../../../api/mutation.create.js';

function CreateCourseDrawer(props) {
  const course = createStore.useSelector(state => state);
  const createCourse = useCreateCourseMutation();
  const [showSubjectPanel, subjectPanelActions] = useToggleState();
  const [showLevelPanel, levelPanelActions] = useToggleState();
  const isSubpanelOpenedRef = useRef(showSubjectPanel || showLevelPanel);
  const [isSubmitting, setSubmitting] = useState(false);
  useEffect(() => {
    isSubpanelOpenedRef.current = showSubjectPanel || showLevelPanel;
  }, [showLevelPanel, showSubjectPanel]);
  const direction = isSubpanelOpenedRef.current ? "backwards" : "forwards";
  const handleClose = () => {
    if (showSubjectPanel) {
      subjectPanelActions.disable();
    } else if (showLevelPanel) {
      levelPanelActions.disable();
    } else {
      props.onClose();
      createStore.dispatch(createStore.actions.reset());
    }
  };
  const handleSubmit = evt => {
    evt.preventDefault();
    if (!course.title) {
      return;
    }
    setSubmitting(true);

    // Attempt to create the course optimisticly and reset internal state once
    // the creation has been successfully completed. In case something goes
    // wrong we do not reset internal state to allow users to easily retry if
    // necessary.
    createCourse.mutateAsync({
      ...course,
      title: course.title ?? ""
    }).then(createdCourse => {
      createStore.dispatch(createStore.actions.reset());

      // open the selected course in the background
      courseBuilderStore.dispatch(courseBuilderStore.actions.selectCourseById(createdCourse.id));

      // ... and once the creation process has been initialized, then close
      // the drawer immediately
      props.onClose();
    }).catch(err => console.error("Failed to create course", err)).then(() => setSubmitting(false));
  };
  return /*#__PURE__*/jsx(Drawer, {
    explicitKey: "create-course-drawer",
    active: props.active,
    onClose: handleClose,
    children: /*#__PURE__*/jsx("div", {
      className: cx(wrapperCls, direction),
      children: /*#__PURE__*/jsxs(TransitionGroup, {
        className: transitionWrapperCls,
        children: [!showLevelPanel && !showSubjectPanel && /*#__PURE__*/jsx(SizeTransition, {
          axis: "vertical",
          swappable: true,
          timeout: panelTransitionDurationMs,
          classNames: panelTransitionCls,
          children: ref => /*#__PURE__*/jsx("div", {
            ref: ref,
            className: panelTransitionCls,
            children: /*#__PURE__*/jsx(BasePanel, {
              onSubmit: handleSubmit,
              onClose: handleClose,
              isSubmitting: isSubmitting,
              enableLevelPanel: levelPanelActions.enable,
              enableSubjectPanel: subjectPanelActions.enable
            })
          })
        }, "base"), showSubjectPanel && /*#__PURE__*/jsx(SizeTransition, {
          axis: "vertical",
          swappable: true,
          timeout: panelTransitionDurationMs,
          classNames: panelTransitionCls,
          children: ref => /*#__PURE__*/jsx("div", {
            ref: ref,
            className: panelTransitionCls,
            children: /*#__PURE__*/jsx(CreateCourseSelectPanel, {
              target: "subjects",
              course: course,
              onSave: newSubjects => {
                createStore.dispatch(createStore.actions.replaceSubjects(newSubjects));
                subjectPanelActions.disable();
              },
              onCancel: subjectPanelActions.disable
            })
          })
        }, "subjects"), showLevelPanel && /*#__PURE__*/jsx(SizeTransition, {
          axis: "vertical",
          swappable: true,
          timeout: panelTransitionDurationMs,
          classNames: panelTransitionCls,
          children: ref => /*#__PURE__*/jsx("div", {
            ref: ref,
            className: panelTransitionCls,
            children: /*#__PURE__*/jsx(CreateCourseSelectPanel, {
              target: "levels",
              course: course,
              onSave: newLevels => {
                createStore.dispatch(createStore.actions.replaceLevels(newLevels));
                levelPanelActions.disable();
              },
              onCancel: levelPanelActions.disable
            })
          })
        }, "levels")]
      })
    })
  });
}

export { CreateCourseDrawer };
//# sourceMappingURL=_create-drawer.js.map
